import React, { useState, useEffect } from "react";
import {
  $Skeleton,
  $Radio,
  $TableTree,
  IOnFilter,
  IOnSort,
  $Tag,
  $MessageBox,
} from "us.common/components";
import { useTranslation } from "react-i18next";
import { IActivityTable } from "./Interfaces";
import {
  getTableData,
  getGroupsOptions,
  getFilterActivities,
  handleLineSeparator,
} from "./Functions";
import { IActivity } from "us.common/interfaces";

/**
 * @description Activity Execution
 * @link Design document - https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/3083632702/Activity+Execution+UI+Implementation
 * @author Rajitha Sanjayamal <rajithasa@unicorn-solutions.com>
 * @since 17/08/2022
 */
const ActivityTable: React.FC<IActivityTable> = (props) => {
  const { t } = useTranslation();
  const { onClose, dataSource, groupingProperty, handleSelect } = props;
  const [filterId, setFilterId] = useState<number | string>(0);
  const { groupingValue, tableGrouping } = groupingProperty;

  useEffect(() => {
    const interval = setInterval(() => {
      const firstRows = document.querySelectorAll(
        ".activity-list .ant-table-row-level-1"
      );
      if (firstRows && firstRows.length > 0) {
        const firstRow = firstRows[0] as HTMLElement;
        firstRow.focus();
        firstRow.classList.add("ant-table-row-selected");
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const handleRowFocus = (row: HTMLElement) => {
    const allRows = document.querySelectorAll(".ant-table-row");
    allRows.forEach((r) => r.classList.remove("ant-table-row-selected"));
    row.classList.add("ant-table-row-selected");
  };

  const columns: any = [
    {
      title: "",
      dataIndex: "group",
      key: "group",
      className: "group-td",
      width: 24,
      customRenderParent: (text: any, record: any, index: number) => {
        return {
          children: text,
          key: index,
          props: {
            colSpan: 4,
          },
        };
      },
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <></>,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COMMON:ACTIVITY_EXECUTION.CODE"),
      dataIndex: "activityCode",
      key: "activityCode",
      width: 120,
      className: "text-nowrap",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <span>{record?.activityCode}</span>,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COMMON:ACTIVITY_EXECUTION.NAME"),
      dataIndex: "displayName",
      key: "displayName",
      className: "text-nowrap",
      ellipsis: true,
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      customRenderChild: (text: any, record: any, index: number) => {
        const { displayName, activityVersion } = record ?? {};
        return {
          children: (
            <div className="d-flex align-items-center justify-content-between">
              <span>{displayName}</span>
            </div>
          ),
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
    {
      title: t("US.COMMON:ACTIVITY_EXECUTION.DESCRIPTION"),
      dataIndex: "description",
      key: "description",
      className: "text-nowrap",
      ellipsis: true,
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: true,
      customRenderChild: (text: any, record: any, index: number) => {
        return {
          children: <span>{record?.description}</span>,
          key: index,
          props: {
            colSpan: 1,
          },
        };
      },
    },
  ];

  /**
   * @function
   * @description handle activity selection
   * @param {IActivity} record selected activity record data.
   */
  const onSelectedActivity = (record: IActivity): void => {
    const { displayName, activityVersion, activityId } = record ?? {};
    try {
      handleSelect(`${displayName}_${activityVersion}_${activityId}`, {
        ...record,
        version: activityVersion,
      });
      onClose();
    } catch (error) {}
  };

  /**
   * @function
   * @description handle table data sorting
   * @param {(data:Array<any>)=>Array<any>} sortData
   * @param {Array<any>} dataSource
   * @returns {IOnSort}
   */
  const handleSort: IOnSort = (sortData, dataSource) => {
    try {
      return sortData(
        dataSource.map((i: any) => {
          return {
            ...i,
            children: sortData(i.children),
          };
        })
      );
    } catch (error) {
      return [];
    }
  };

  /**
   * @function
   * @description handle table data sorting
   * @param {(data:Array<any>)=>Array<any>} searchData
   * @param {Array<any>} dataSource
   * @returns {IOnFilter}
   */
  const handleFilter: IOnFilter = (searchData, dataSource) => {
    let result: any[] = [];
    try {
      dataSource.flatMap((i: any) => {
        searchData(i?.children)?.length != 0 &&
          result.push({
            ...i,
            children:
              searchData(i?.children)?.length == 0
                ? []
                : searchData(i?.children),
          });
      });
      return result;
    } catch (error) {
      return result;
    }
  };

  return (
    <div className="d-flex select-activity-body">
      <div className={`activity-group ${handleLineSeparator(dataSource)}`}>
        <p>
          <strong>{t("US.COMMON:ACTIVITY_EXECUTION.ACTIVITY_GROUP")}</strong>
        </p>
        <div>
          <$Radio
            name="filter"
            optionText="label"
            optionValue="value"
            className=""
            size="small"
            value={filterId}
            buttonStyle="solid"
            optionStyle=""
            options={getGroupsOptions(dataSource, groupingProperty)}
            onChange={(e: any) => {
              setFilterId(e.target.value);
            }}
          />
        </div>
      </div>
      <div className="flex-grow-1 activity-list default-focus-none">
        <$Skeleton loading={false} active paragraph={{ rows: 2 }}>
          <$TableTree
            onRow={(record: any, index: any) => {
              return {
                tabIndex: index,
                onClick: (event: any) => {
                  const row = event.currentTarget as HTMLElement;
                  row?.focus();
                  handleRowFocus(row);
                },
                onDoubleClick: (event: any) => {
                  onSelectedActivity(record);
                },
                onKeyDown: (e: any) => {
                  e.preventDefault();
                  try {
                    if (e.key === "ArrowUp") {
                      const prevRow = e.target.previousSibling as HTMLElement;
                      if (prevRow) {
                        prevRow.focus();
                        handleRowFocus(prevRow);
                      }
                    }
                    if (e.key === "ArrowDown") {
                      const nextRow = e.target.nextSibling as HTMLElement;
                      if (nextRow) {
                        nextRow.focus();
                        handleRowFocus(nextRow);
                      }
                    }
                    if (e.key === "Enter") onSelectedActivity(record);
                  } catch (err) {
                    // failure
                    $MessageBox(
                      "error",
                      "US.COMMON:COMMON.SHORTCUT_ACTION_FAILED",
                      "",
                      ""
                    );
                  }
                },
              };
            }}
            rowKey={(record: any) => {
              return record.key;
            }}
            onSort={handleSort}
            onFilter={handleFilter}
            filterOnType={true}
            data={getTableData(
              getFilterActivities(dataSource, filterId, groupingValue),
              tableGrouping
            )}
            columns={columns}
            size="small"
            className="activity-table"
            bordered
            pagination={{ defaultPageSize: 20 }}
            scroll={{ x: 780, y: "calc(100vh - 170px)" }}
            expandable={{
              defaultExpandAllRows: true,
            }}
            resetOnSourceChange={true}
          />
        </$Skeleton>
      </div>
    </div>
  );
};

export default ActivityTable;
